<main class="departures-board">
  <header class="board-header">
    <div class="brand-block">
      <img src="assets/branding/logo.png" alt="Riya Shuma">
      <div>
        <p>Live travel information</p>
        <h1>Departures</h1>
      </div>
    </div>

    <div class="header-controls">
      <label>
        <span>Operator</span>
        <select [(ngModel)]="selectedOperatorId" (ngModelChange)="onOperatorChange()">
          <option [ngValue]="null">All operators</option>
          <option *ngFor="let operator of operators" [ngValue]="getOperatorId(operator)">
            {{ getOperatorName(operator) }}
          </option>
        </select>
      </label>
      <div class="clock" aria-live="off">
        <strong>{{ currentTime | date:'HH:mm:ss' }}</strong>
        <span>{{ currentTime | date:'EEEE, d MMMM y' }}</span>
      </div>
      <a routerLink="/login" class="login-link">
        <i class="material-icons" aria-hidden="true">lock</i>
        Login
      </a>
    </div>
  </header>

  <div class="update-alert" *ngIf="errorMessage" role="status">
    <i class="material-icons" aria-hidden="true">wifi_off</i>
    {{ errorMessage }}
  </div>

  <section class="shared-link-state" *ngIf="isSharedTripLoading" aria-live="polite">
    <i class="material-icons spin" aria-hidden="true">sync</i>
    <div><h2>Opening shared trip…</h2><p>Live departures are continuing to load.</p></div>
  </section>

  <section class="shared-link-state shared-link-error" *ngIf="sharedTripUnavailable">
    <i class="material-icons" aria-hidden="true">link_off</i>
    <div>
      <h2>This trip link is unavailable</h2>
      <p>It may have expired or the address may be incomplete.</p>
      <a routerLink="/departures">Back to live traveller information</a>
    </div>
  </section>

  <section class="board-content" aria-label="Public departures">
    <div class="desktop-board">
      <div class="board-row board-columns" role="row">
        <span>Departure</span>
        <span>Destination</span>
        <span>Operator</span>
        <span>Vehicle</span>
        <span>Departs</span>
        <span>Arrives</span>
        <span>Next stop</span>
        <span>ETA</span>
        <span>Status</span>
      </div>

      <div class="loading-state" *ngIf="isLoading && !departures.length">
        <i class="material-icons" aria-hidden="true">sync</i>
        Loading live departures…
      </div>

      <div class="board-row departure-row" *ngFor="let item of visibleDepartures; trackBy: trackByTripId" role="button" tabindex="0" (click)="selectTrip(item)" (keydown.enter)="selectTrip(item)" (keydown.space)="$event.preventDefault(); selectTrip(item)">
        <strong>{{ item.origin.name | uppercase }}</strong>
        <strong>{{ item.destination.name | uppercase }}</strong>
        <span class="operator-cell">
          <span class="logo-shell" *ngIf="item.operator.logoUrl">
            <img [src]="item.operator.logoUrl" [alt]="item.operator.name + ' logo'" (error)="useLogoFallback($event)">
          </span>
          <span>{{ item.operator.name }}</span>
        </span>
        <span class="vehicle-cell">
          <strong>{{ getVehicleIdentification(item) }}</strong>
          <small *ngIf="item.vehicle.registration">{{ item.vehicle.registration }}</small>
        </span>
        <time [dateTime]="item.scheduledDepartureUtc"><small>{{ getDayLabel(item.scheduledDepartureUtc) }}</small>{{ item.scheduledDepartureUtc | date:'HH:mm' }}</time>
        <time [dateTime]="item.scheduledArrivalUtc"><small>{{ getDayLabel(item.scheduledArrivalUtc) }}</small>{{ item.scheduledArrivalUtc | date:'HH:mm' }}</time>
        <span>{{ hasNextStop(item) ? item.nextStop?.name : '—' }}</span>
        <span [title]="getEtaTitle(item)" [class.eta-stale]="item.nextStop?.etaConfidence === 'stale'">
          {{ getEtaText(item) }}
        </span>
        <span class="status" [attr.data-severity]="item.status.severity" [attr.data-code]="item.status.code">
          {{ getStatusLabel(item) }}
        </span>
      </div>
    </div>

    <div class="mobile-board">
      <article class="departure-card" *ngFor="let item of departures; trackBy: trackByTripId" role="button" tabindex="0" (click)="selectTrip(item)" (keydown.enter)="selectTrip(item)" (keydown.space)="$event.preventDefault(); selectTrip(item)">
        <div class="card-route">
          <div><small>From</small><strong>{{ item.origin.name | uppercase }}</strong></div>
          <i class="material-icons" aria-hidden="true">arrow_forward</i>
          <div><small>To</small><strong>{{ item.destination.name | uppercase }}</strong></div>
        </div>
        <div class="card-operator">
          <img *ngIf="item.operator.logoUrl" [src]="item.operator.logoUrl" [alt]="item.operator.name + ' logo'" (error)="useLogoFallback($event)">
          <strong>{{ item.operator.name }}</strong>
          <span class="status" [attr.data-severity]="item.status.severity">{{ getStatusLabel(item) }}</span>
        </div>
        <dl>
          <div><dt>Departs</dt><dd>{{ getDayLabel(item.scheduledDepartureUtc) }} {{ item.scheduledDepartureUtc | date:'HH:mm' }}</dd></div>
          <div><dt>Arrives</dt><dd>{{ getDayLabel(item.scheduledArrivalUtc) }} {{ item.scheduledArrivalUtc | date:'HH:mm' }}</dd></div>
          <div><dt>Vehicle ID</dt><dd>{{ getVehicleIdentification(item) }}<small *ngIf="item.vehicle.registration">Registration: {{ item.vehicle.registration }}</small></dd></div>
          <div><dt>Next stop</dt><dd>{{ hasNextStop(item) ? item.nextStop?.name : '—' }}</dd></div>
          <div><dt>Next ETA</dt><dd>{{ getEtaText(item) }}</dd></div>
        </dl>
      </article>
    </div>

    <div class="empty-state" *ngIf="!isLoading && !departures.length && !errorMessage">
      <i class="material-icons" aria-hidden="true">event_available</i>
      <h2>No departures currently listed</h2>
      <p>Please check again shortly.</p>
    </div>
  </section>

  <div class="drawer-backdrop" *ngIf="selectedTrip" (click)="closeTripDetails()"></div>
  <aside #tripDrawer class="trip-drawer" *ngIf="selectedTrip as trip" aria-labelledby="trip-detail-title" tabindex="-1">
    <header>
      <div>
        <p>Trip details</p>
        <h2 id="trip-detail-title">{{ trip.tripName || (trip.origin.name + ' to ' + trip.destination.name) }}</h2>
      </div>
      <div class="drawer-header-actions">
        <button class="share-trip-button" type="button" (click)="shareTrip(trip)" [disabled]="isSharing">
          <i class="material-icons" aria-hidden="true">{{ isSharing ? 'sync' : 'share' }}</i>
          {{ isSharing ? 'Sharing…' : 'Share trip' }}
        </button>
        <button class="close-trip-button" type="button" (click)="closeTripDetails()" aria-label="Close trip details">
          <i class="material-icons" aria-hidden="true">close</i>
        </button>
      </div>
    </header>

    <p class="live-unavailable-note" *ngIf="sharedTripFallback && !isSharedTripLive">
      <i class="material-icons" aria-hidden="true">info</i>
      This trip is not currently on the live departures board. Showing its shared schedule; live status and ETA are currently unavailable.
    </p>

    <div class="drawer-operator">
      <img *ngIf="trip.operator.logoUrl" [src]="trip.operator.logoUrl" [alt]="trip.operator.name + ' logo'" (error)="useLogoFallback($event)">
      <div><small>Operator</small><strong>{{ trip.operator.name }}</strong></div>
      <span class="status" [attr.data-severity]="trip.status.severity">{{ getStatusLabel(trip) }}</span>
    </div>

    <div class="drawer-route">
      <div><i class="material-icons" aria-hidden="true">trip_origin</i><span><small>Departure</small><strong>{{ trip.origin.name | uppercase }}</strong></span></div>
      <div class="route-line"></div>
      <div><i class="material-icons" aria-hidden="true">location_on</i><span><small>Destination</small><strong>{{ trip.destination.name | uppercase }}</strong></span></div>
    </div>

    <dl class="drawer-details">
      <div><dt>Departs</dt><dd>{{ getDayLabel(trip.scheduledDepartureUtc) }} at {{ trip.scheduledDepartureUtc | date:'HH:mm' }}</dd></div>
      <div><dt>Arrives</dt><dd>{{ getDayLabel(trip.scheduledArrivalUtc) }} at {{ trip.scheduledArrivalUtc | date:'HH:mm' }}</dd></div>
      <div><dt>Vehicle identification number</dt><dd>{{ getVehicleIdentification(trip) }}</dd></div>
      <div *ngIf="trip.vehicle.registration"><dt>Registration</dt><dd>{{ trip.vehicle.registration }}</dd></div>
      <div><dt>Next stop</dt><dd>{{ hasNextStop(trip) ? trip.nextStop?.name : 'Not available' }}</dd></div>
      <div><dt>Next-stop ETA</dt><dd>{{ getEtaText(trip) }}</dd></div>
      <div *ngIf="trip.trackingUpdatedUtc"><dt>Tracking updated</dt><dd>{{ trip.trackingUpdatedUtc | date:'HH:mm:ss' }}</dd></div>
      <div *ngIf="trip.routeName"><dt>Route</dt><dd>{{ trip.routeName }}</dd></div>
      <div *ngIf="trip.seatsAvailable !== null && trip.seatsAvailable !== undefined"><dt>Seats available</dt><dd>{{ trip.seatsAvailable }}</dd></div>
    </dl>

    <section class="shared-stops" *ngIf="trip.stops?.length">
      <h3>Stops</h3>
      <ol>
        <li *ngFor="let stop of trip.stops; trackBy: trackByStop">{{ stop.stopName || stop.name }}</li>
      </ol>
    </section>

    <p class="share-error" *ngIf="shareError" role="alert">{{ shareError }}</p>
    <div class="share-fallback" *ngIf="fallbackShareUrl">
      <label for="fallback-share-link">Trip link</label>
      <div>
        <input #fallbackLink id="fallback-share-link" type="text" readonly [value]="fallbackShareUrl">
        <button type="button" (click)="copyFallbackLink()">Copy link</button>
      </div>
    </div>

    <p class="drawer-note" *ngIf="!sharedTripFallback && !trip.actualDepartureUtc">Next-stop information will appear after the vehicle reaches the first stop and the trip begins.</p>
    <p class="drawer-note" *ngIf="trip.status.code === 'COMPLETED' || trip.actualArrivalUtc">This trip has reached its final stop.</p>
  </aside>

  <div class="share-toast" [class.error]="notificationIsError" *ngIf="notificationMessage" [attr.role]="notificationIsError ? 'alert' : 'status'" aria-live="polite">
    <i class="material-icons" aria-hidden="true">{{ notificationIsError ? 'error' : 'check_circle' }}</i>
    {{ notificationMessage }}
  </div>

  <footer class="board-footer">
    <span>
      <i class="material-icons" aria-hidden="true">sync</i>
      Updated {{ generatedAt ? (generatedAt | date:'HH:mm:ss') : '—' }}
    </span>
    <span *ngIf="pageCount > 1" class="page-dots" aria-label="Departures page">
      <i *ngFor="let index of pageIndexes" [class.active]="index === currentPage"></i>
    </span>
    <span>Times shown in your local time</span>
  </footer>
</main>
